<template>
  <section class="cs-sign-in">
    <loader ref="loader" />
    <div class="cs-sign-in__title-wrapper">
      <h1 class="cs-sign-in__title">{{ $t("sign-in.welcome") }}</h1>
      <p class="cs-sign-in__subtitle">
        {{ $t("sign-in.already-have-account") }}
      </p>
    </div>

    <div v-if="isPasswordMode">
      <Form @submit="signIn">
        <div class="cs-sign-in__inputs">
          <text-input
            id="email"
            name="email"
            v-model:value.trim="email"
            :placeholder="$t('sign-in.email')"
            ref="email"
            :schema="emailSchema"
            autocomplete="email"
          />
          <text-input
            id="password"
            v-model:value.trim="password"
            name="password"
            :schema="passwordSchema"
            :type="!displayPassword ? 'password' : 'text'"
            :placeholder="$t('sign-in.password')"
            :imgSource="
              !displayPassword
                ? require('@/assets/img/eye-slash.svg')
                : require('@/assets/img/eye.svg')
            "
            @handle-input-icon="toggleDisplayPassword"
          />
        </div>
        <div class="cs-sign-in__toggle cs-sign-in__toggle--inputs">
          <button
            type="button"
            class="cs-sign-in__toggle-btn"
            :class="{ 'is-active': isPasswordMode }"
            @click="setAuthMode('password')"
          >
            {{ $t("sign-in.use-password") }}
          </button>
          <button
            type="button"
            class="cs-sign-in__toggle-btn"
            :class="{ 'is-active': !isPasswordMode }"
            @click="setAuthMode('magic')"
          >
            {{ $t("sign-in.send-verification-code") }}
          </button>
        </div>
        <small class="cs-sign-in__error" v-if="formError">
          {{ formError }}
        </small>
        <CustomButton
          className="cs-sign-in__primary button-default"
          type="submit"
          :name="$t('sign-in.sign-in')"
        />
      </Form>
      <p class="cs-sign-in__hint">
        {{ $t("sign-in.password-hint") }}
      </p>
    </div>

    <div v-else class="cs-sign-in__magic">
      <MagicLink
        :submitLabel="$t('sign-in.send')"
        :compact="true"
        :isPasswordMode="isPasswordMode"
        @change-mode="handleMagicModeChange"
        @offMagic="handleMagicBack"
      />
      <p class="cs-sign-in__hint">
        {{ $t("sign-in.verification-hint") }}
      </p>
    </div>

    <div class="cs-sign-in__link-box">
      <a
        target="_blank"
        :href="resetUrl"
        class="cs-sign-in__link"
        @click.prevent="onForgetPassword"
      >
        {{ $t("sign-in.forgot-password") }}
      </a>
    </div>

    <div v-if="showGuestButton" class="cs-sign-in__divider">
      <span class="cs-sign-in__divider-line" />
      <span class="cs-sign-in__divider-text">{{ $t("sign-in.or") }}</span>
      <span class="cs-sign-in__divider-line" />
    </div>

    <CustomButton
      v-if="showGuestButton"
      className="cs-sign-in__guest button-default"
      type="button"
      :name="$t('sign-in.continue-as-guest')"
      @handle-click="loginAsGuest"
    />
    <small class="cs-sign-in__error" v-if="guestError">
      {{ guestError }}
    </small>

    <div class="cs-sign-in__signup">
      {{ $t("sign-in.new-to-rocketfuel") }}
      <span @click="doSignUp()" class="cs-sign-in__signup-link">
        {{ $t("sign-in.create-account") }}
      </span>
    </div>
    <WhiteLabelTheme />
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import MagicLink from "@/components/magicLink.vue";
import WhiteLabelTheme from "@/components/WhiteLabelTheme.vue";
import { SIGN_IN_HASHED, LOGIN_AS_GUEST } from "@/store/modules/auth/consts";

import {
  GET_USER_INFO,
  SET_TAB,
  USER_TRACK_EVENTS,
} from "@/store/modules/user/consts";
import CustomButton from "./customButton.vue";
import * as Yup from "yup";
import { extractCartEmail, persistCartEmail } from "@/utils/cartEmail";
import getRecaptchaToken from '@/utils/recaptcha';
export default {
  components: {
    CustomButton,
    MagicLink,
    WhiteLabelTheme,
  },
  data() {
    return {
      popup: null,
      email: this.$store.getters["auth/userEmail"] || "",
      password: "",
      formError: "",
      guestError: "",
      resetUrl:
        process.env.VUE_APP_RESET_URL ||
        "https://app.rocketfuelblockchain.com/reset",
      displayPassword: false,
      authMode: "password",
    };
  },
  setup() {
    const emailSchema = Yup.string()
      .email("Invalid email")
      .required("Please enter you email");
    const passwordSchema = Yup.string().required("Please enter your password");
    return { emailSchema, passwordSchema };
  },
  computed: {
    ...mapGetters({
      marketInfo: "user/marketInfo",
      cart: "user/cart",
      tab: "user/tab",
      userEmail: "auth/userEmail",
    }),
    isPasswordMode() {
      return this.authMode === "password";
    },
    showGuestButton() {
      // Hide guest button if GUEST_USER_TX_AMOUNT_LIMIT is set (non-zero)
      const guestTxAmountLimit = process.env.VUE_APP_GUEST_USER_TX_AMOUNT_LIMIT;
      if (!guestTxAmountLimit || Number(guestTxAmountLimit) === 0) {
        return true; // Show button if limit is not set
      }

      // Hide button if order amount exceeds the limit
      const orderAmount = this.cart?.totalAmount || this.cart?.amount || 0;
      return Number(orderAmount) <= Number(guestTxAmountLimit);
    },
    merchantAuth() {
      return (
        this.cart?.merchantAuth || this.cart?.customerInfo?.merchantAuth || ""
      );
    },
    title() {
      if (
        this.marketInfo &&
        this.marketInfo.whiteLabel &&
        this.marketInfo.whiteLabel.colorSchema &&
        this.marketInfo.whiteLabel.colorSchema.partnerLabel
      ) {
        return this.marketInfo.whiteLabel.colorSchema.partnerLabel;
      }
      return "Rocketfuel";
    },
  },
  watch: {
    userEmail: {
      handler(val) {
        if (val) {
          this.email = val;
        }
      },
      immediate: true,
    },
    cart: {
      handler(val) {
        if (!val || !Object.keys(val).length) {
          return;
        }
        persistCartEmail(this.$store, val);
        this.syncEmailFromCart();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch(`user/${SET_TAB}`, "");
    this.$refs.loader.hide();
    this.syncEmailFromCart();
    setTimeout(() => {
      // this.$refs.email.$el.focus();
    }, 500);
  },
  methods: {
    syncEmailFromCart() {
      const email =
        this.$store.getters["auth/userEmail"] || extractCartEmail(this.cart);
      if (email) {
        this.email = email;
      }
    },
    setAuthMode(mode) {
      if (this.authMode === mode) {
        return;
      }
      if (mode === "magic") {
        this.$store.commit("auth/setUserEmail", this.email);
      }
      if (mode === "password") {
        this.email = this.$store.getters["auth/userEmail"] || this.email;
      }
      this.formError = "";
      this.guestError = "";
      this.authMode = mode;
    },
    handleMagicBack() {
      this.setAuthMode("password");
    },
    handleMagicModeChange(mode) {
      this.setAuthMode(mode);
    },
    async signIn() {
      this.formError = "";
      this.guestError = "";
      const pendingConnect = this.$DbStorage.getItem("rocketfuel_connect");
      if (pendingConnect) {
        this.$DbStorage.removeItem("rocketfuel_connect");
        switch (pendingConnect) {
          case "coinbase":
            // this.popup = window.open(
            //   "",
            //   "exchange_connect10",
            //   `width=585, height=800, top=${
            //     (window.screen.height - 800) / 2
            //   }, left=${(window.screen.width - 585) / 2}, _blank`
            // );
            break;
          case "binance":
            break;
          case "gemini":
            break;
          case "kraken":
            break;
          case "bank":
            break;
          default:
            break;
        }
      }

      this.$nextTick(async () => {
        this.$refs.loader.start();
        let merchantId = null;
        if (this.cart && this.cart.merchant_id) {
          merchantId = btoa(this.cart.merchant_id);
        }
        const encryptedReq = this.encryptStringWithRSA(
          JSON.stringify({
            merchantId,
            email: this.email.toLowerCase(),
            password: this.password,
          })
        );
        try {
          const recaptcha = await getRecaptchaToken(this);
          await this.$store.dispatch(`auth/${SIGN_IN_HASHED}`, {
            encryptedReq,
            recaptcha,
          });
          const info = await this.$store.dispatch(`user/${GET_USER_INFO}`);
          if (info.type === 1 || info.type === 5) {
            //merchant or payout admin
            if (this.popup) {
              this.popup.focus();
              this.popup.close();
              this.popup = null;
            }
            this.$store.commit("auth/logOut");
            throw new Error("This email cannot be used for payment");
          }
          if (this.$store.getters["auth/userStatus"] !== 1) {
            if (this.popup) {
              this.popup.focus();
              this.popup.close();
              this.popup = null;
            }
            this.$store.commit("auth/logOut");
            throw new Error("Please, confirm your email");
          }
          if (!Object.values(this.$store.getters["user/cart"]).length) {
            this.notifyParentWindow({
              type: "rocketfuel_get_cart",
            });
          }
          this.$store.dispatch(`user/${USER_TRACK_EVENTS}`, {
            moduleInfo: {
              current: "Login",
              activity: "signin with password button clicked",
            },
          });
          this.$router.push({ name: "Payment" });
        } catch (e) {
          if (this.popup) {
            this.popup.focus();
            this.popup.close();
            this.popup = null;
          }
          if (e.response) {
            if (e.response.data.message === "Invalid OTP") {
              this.$store.dispatch(`user/${USER_TRACK_EVENTS}`, {
                moduleInfo: {
                  current: "Login",
                  activity: "signin with password button clicked",
                },
              });
              this.$router.push({
                name: "Totp",
                params: {
                  data: this.Encrypt(
                    JSON.stringify({
                      email: this.email.toLowerCase(),
                      password: this.password,
                    })
                  ),
                },
              });
            } else {
              this.formError = e.response.data.message;
            }
          } else {
            this.formError = e.message;
          }
          this.$refs.loader.end();
        }
      });
    },
    async loginAsGuest() {
      this.formError = "";
      this.guestError = "";
      if (!this.merchantAuth) {
        this.guestError = this.$t("sign-in.guest-error");
        return;
      }
      this.$refs.loader.start();
      try {
        await this.$store.dispatch(`auth/${LOGIN_AS_GUEST}`, {
          merchantAuth: this.merchantAuth,
        });
        await this.$store.dispatch(`user/${GET_USER_INFO}`);
        await this.$store.dispatch(`user/${USER_TRACK_EVENTS}`, {
          moduleInfo: {
            current: "Login",
            activity: "continue as guest button clicked",
          },
        });
        this.$router.push({ name: "Payment" });
      } catch (error) {
        this.guestError =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("sign-in.guest-error");
      } finally {
        this.$refs.loader.end();
      }
    },
    doSignUp() {
      this.$router.push({
        name: "SignUp",
      });
    },
    onForgetPassword() {
      this.$router.push({
        name: "ForgetPassword",
        query: { lastEmail: this.email },
      });
    },
    toggleDisplayPassword() {
      this.displayPassword = !this.displayPassword;
    },
  },
};
</script>

<style lang="scss">
.cs-sign-in {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 36px 16px 24px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: center;
  gap: 4px;

  &__logo-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
  }

  &__logo {
    width: 42px;
    height: 42px;
  }

  &__title-wrapper {
    margin-bottom: 0;
  }

  &__title {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.1;
    color: #1b1f3b;
    margin-bottom: 8px;
  }

  &__subtitle {
    font-size: 15px;
    font-weight: 600;
    color: #6e728b;
    margin: 0;
  }

  &__toggle {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    background: #edf0f7;
    border-radius: 999px;
    padding: 3px;
    gap: 4px;
    border: 1px solid #d8dce6;
    margin-top: 12px;
  }
  &__toggle--inputs {
    margin-top: 8px;
  }

  &__toggle-btn {
    border: none;
    border-radius: 999px;
    background: transparent;
    padding: 8px 10px;
    font-size: 13px;
    font-weight: 600;
    color: #6e728b;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
    }

    &.is-active {
      background: #ffffff;
      color: #1b1f3b;
      box-shadow: 0 3px 6px rgba(30, 36, 66, 0.16);
    }
  }

  &__inputs {
    display: grid;
    gap: 10px;
    text-align: left;
  }

  &__magic {
    display: flex;
    flex-direction: column;
  }

  &__primary {
    width: 100%;
    margin-top: 12px;
    padding: 10px 0 !important;
    font-weight: 600 !important;
    font-size: 15px;
  }

  &__hint {
    margin-top: 6px;
    font-size: 12px;
    color: #6e728b;
  }

  &__error {
    display: block;
    margin-top: 12px;
    color: $red500;
    font-size: 14px;
    line-height: 1.2;
  }

  &__link-box {
    margin: 0;
  }

  &__link {
    font-size: 14px;
    font-weight: 600;
    color: #f0833c;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__divider {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__divider-line {
    flex-grow: 1;
    height: 1px;
    background: #e3e6f0;
  }

  &__divider-text {
    font-size: 12px;
    font-weight: 600;
    color: #959abd;
    letter-spacing: 0.08em;
  }

  &__guest {
    width: 100%;
    padding: 10px 0 !important;
    font-weight: 600 !important;
    font-size: 15px;
  }

  &__signup {
    margin-top: 8px;
    font-size: 14px;
    color: #6e728b;
  }

  &__signup-link {
    color: #f0833c;
    font-weight: 600;
    margin-left: 4px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__powered-by {
    margin-top: 36px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px 24px;
  }
}
</style>
